export type AgencyClinicDisplayInput = {
  id: string;
  name: string;
  clinic_key?: string | null;
  branches?: Array<{ [key: string]: unknown }> | null;
  city?: string | null;
  state?: string | null;
};

export type AgencyClinicDisplayOptions = {
  agencyName?: string | null;
  totalClinicCount?: number;
};

function getFirstBranchName(
  branches?: Array<{ [key: string]: unknown }> | null
): string | null {
  for (const branch of branches ?? []) {
    const name = branch.name;
    if (typeof name === 'string' && name.trim()) {
      return name.trim();
    }
  }
  return null;
}

/**
 * Human-readable label for an agency clinic. VirtualizedFormSelect drops options
 * with blank labels, so callers must use this (not raw `clinic.name`) in dropdowns.
 */
export function getAgencyClinicDisplayName(
  clinic: AgencyClinicDisplayInput,
  options?: AgencyClinicDisplayOptions
): string {
  const trimmedName = clinic.name?.trim();
  if (trimmedName) return trimmedName;

  const branchName = getFirstBranchName(clinic.branches);
  if (branchName) return branchName;

  const clinicKey = clinic.clinic_key?.trim();
  if (clinicKey) {
    if (options?.totalClinicCount === 1) {
      return 'Default Clinic';
    }
    return `Clinic ${clinicKey}`;
  }

  const location = [clinic.city, clinic.state]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');
  if (location) return location;

  if (options?.totalClinicCount === 1 && options.agencyName?.trim()) {
    return options.agencyName.trim();
  }

  return `Clinic (${clinic.id.slice(0, 8)})`;
}

export function buildAgencyClinicSelectOptions(
  clinics: AgencyClinicDisplayInput[],
  options?: AgencyClinicDisplayOptions
): Array<{ value: string; label: string }> {
  const totalClinicCount = options?.totalClinicCount ?? clinics.length;
  return clinics.map(clinic => ({
    value: clinic.id,
    label: getAgencyClinicDisplayName(clinic, {
      ...options,
      totalClinicCount,
    }),
  }));
}
